<script setup>
import {
  TEXT_ELEMENT_TAGS,
  TEXT_ELEMENT_VARIANTS,
  VARIANT_FONT_SIZE_MAP,
  VARIANT_MOBILE_FONT_SIZE_MAP,
} from "@/v2/primitives/Text/constants.js";
import useFontSize from "@/util/useFontSize.js";
import useWindowDimensions from "@/util/useWindowDimensions.ts";
import * as viewportUtil from "@/util/viewportUtil.ts";
import { computed, useSlots } from "vue";

const props = defineProps({
  as: {
    type: String,
    default: "p",
    validator(val) {
      return TEXT_ELEMENT_TAGS.includes(val);
    },
  },
  variant: {
    type: String,
    required: true,
    validator(val) {
      return TEXT_ELEMENT_VARIANTS.includes(val);
    },
  },
  useScaling: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const slots = useSlots();
const { calculateFontSize } = useFontSize();
const { innerWidth, innerHeight } = useWindowDimensions();

const componentClasses = computed(() => ({
  [`text text--${props.variant}`]: true,
  "text--use-scaling": props.useScaling,
}));

const componentStyle = computed(() => {
  const fontSizeMap = viewportUtil.isMobilePortrait({
    w: innerWidth.value,
    h: innerHeight.value,
  })
    ? VARIANT_MOBILE_FONT_SIZE_MAP
    : VARIANT_FONT_SIZE_MAP;

  if (
    props.useScaling &&
    fontSizeMap[props.variant] &&
    !document.documentElement.classList.contains("massive-screen")
  ) {
    const { beginScale, maxFontSize, scale, factor } =
      fontSizeMap[props.variant];

    return calculateFontSize(
      slots.default()[0].children,
      beginScale,
      maxFontSize,
      scale,
      factor
    );
  }

  return {};
});
</script>

<template>
  <component :is="as" :class="componentClasses" :style="componentStyle">
    <slot />
  </component>
</template>

<style scoped lang="scss">
@import "./text-variants";

.text {
  @include text-default;

  &--display1 {
    @include text-display1;
  }

  &--display2 {
    @include text-display2;
  }

  &--title1 {
    @include text-title1;
  }

  &--title2 {
    @include text-title2;
  }

  &--title3 {
    @include text-title3;
  }

  &--title4 {
    @include text-title4;
  }

  &--body-large {
    @include text-body-large;
  }

  &--body {
    @include text-body;
  }

  &--body-small {
    @include text-body-small;
  }
}
</style>
